import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import AddSocailMediaModel from "./AddSocailMediaModel";
import Iterator from "../utils/Iterator";
import { indexStore } from "src/store/indexStore";
import { socialMediaTypes, SocialMediaIcons } from "src/enums/socialMediaType";
import EditSocialMediaModel from "./EditSocialMediaModel";
import { Skeleton } from "../ui/skeleton";
import Visibility from "../utils/Visibility";
import { userTypes } from "src/enums/usersTpes";
import { Card, CardContent } from "../ui/card";
import { Share2, Sparkles, Edit, Trash2 } from "lucide-react";
import { AlertDialogs } from "../Dialgos/Alter-Daylogs";

function SocialMedia(props) {
  const { t } = useTranslation();
  const {
    socialMedia,
    setSocialMediaElement,
    deleteSocialMediaFunction,
    socialMediaLoading,
    fetchSocialMediaFunction,
  } = indexStore();
  useEffect(() => {
    fetchSocialMediaFunction();
  }, [fetchSocialMediaFunction]);

  const per = localStorage.getItem("role");
  const canEdit = per === userTypes.ADMIN || per === userTypes.DATAMANAGER;

  return (
    <div className="flex flex-col gap-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Share2 className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground">
              {t("gloabl.socialMedia")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t("gloabl.manage_social_media") ||
                "Manage your social media links and profiles"}
            </p>
          </div>
        </div>
        <Visibility visible={canEdit}>
          <AddSocailMediaModel
            trigger={
              <Button size="lg" className="gap-2">
                <Share2 className="w-4 h-4" />
                {t("gloabl.add")} {t("gloabl.socialMedia")}
              </Button>
            }
          />
        </Visibility>
      </div>

      {/* Social Media Grid */}
      <div className="flex-1">
        <Visibility visible={!socialMediaLoading && socialMedia.length > 0}>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold">
                  {t("gloabl.social_media_found") || "Social Media"} (
                  {socialMedia.length})
                </h3>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              <Iterator
                data={socialMedia}
                render={(element) => (
                  <SocialMeidaCard
                    t={t}
                    element={element}
                    key={element.id}
                    setSocialMediaElement={setSocialMediaElement}
                    deleteSocialMediaFunction={deleteSocialMediaFunction}
                    canEdit={canEdit}
                  />
                )}
              />
            </div>
          </div>
        </Visibility>

        <Visibility visible={socialMediaLoading}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <Iterator
              data={[1, 2, 3, 4, 5, 6]}
              render={(e, i) => <SocialMediaCardLoading key={i} />}
            />
          </div>
        </Visibility>

        <Visibility visible={!socialMediaLoading && socialMedia.length === 0}>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Share2 className="w-16 h-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {t("gloabl.no_social_media_found") || "No social media found"}
              </h3>
              <p className="text-sm text-muted-foreground text-center mb-4">
                {t("gloabl.no_social_media_description") ||
                  "Add your social media links to connect with your customers"}
              </p>
              <Visibility visible={canEdit}>
                <AddSocailMediaModel
                  trigger={
                    <Button size="lg" className="gap-2">
                      <Share2 className="w-4 h-4" />
                      {t("gloabl.add")} {t("gloabl.socialMedia")}
                    </Button>
                  }
                />
              </Visibility>
            </CardContent>
          </Card>
        </Visibility>
      </div>
    </div>
  );
}

export default SocialMedia;
const SocialMeidaCard = ({
  element,
  t,
  setSocialMediaElement,
  deleteSocialMediaFunction,
  canEdit,
}) => {
  const IconComponent = SocialMediaIcons[element.type];
  const platformName = socialMediaTypes.find((e) => e.id == element.type)?.name || "";

  const editRow = (element) => {
    setSocialMediaElement(element);
  };
  const onDelete = (element) => {
    deleteSocialMediaFunction(element.id);
  };

  return (
    <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden border-2 hover:border-primary/20">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            {/* Icon */}
            <div className="p-3 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 shrink-0">
              {IconComponent && (
                <IconComponent className="w-8 h-8 text-primary" />
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-foreground mb-2 line-clamp-1">
                {element.title}
              </h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground font-medium">
                    {t("gloabl.url")}:
                  </span>
                </div>
                <a
                  href={element.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors break-all"
                >
                  <span className="truncate">{element.link}</span>
                  <Share2 className="w-3 h-3 shrink-0" />
                </a>
              </div>
            </div>
          </div>

          {/* Actions */}
          <Visibility visible={canEdit}>
            <div className="flex gap-1 shrink-0">
              <EditSocialMediaModel
                trigger={
                  <button
                    onClick={() => editRow(element)}
                    className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                    title={t("gloabl.edit")}
                  >
                    <Edit className="w-4 h-4 text-muted-foreground" />
                  </button>
                }
              />
              <AlertDialogs
                trigger={
                  <button
                    className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                    title={t("gloabl.delete")}
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                }
                variant="icon"
                onClickOk={() => onDelete(element)}
                description={<span>{t("gloabl.deleteMessage")}</span>}
                title={`${t("gloabl.delete")} ${t("gloabl.socialMedia")}?`}
              />
            </div>
          </Visibility>
        </div>
      </CardContent>
    </Card>
  );
};

const SocialMediaCardLoading = () => {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <Skeleton className="w-16 h-16 rounded-xl shrink-0" />
          <div className="flex-1 space-y-3">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
